import { Drawer, Checkbox, Empty, Spin } from "antd";
import styles from "./styles.module.scss";
import DebouncedSearch from "components/inputs/DebouncedSearch";
import UserInfo from "components/cards/UserInfo";
import Button from "components/general/Button";
import { IoMdClose } from "react-icons/io";
import Text from "components/general/Text";
import { useCallback, useEffect, useState } from "react";
import {
    useGetPreviewUsersInfiniteQuery,
    useUpdateRolePermissionsMutation,
    useUpdateRoleUsersMutation,
    useUpdateSuperUsersMutation,
} from "apis/services/permissions";
import showSuccessMsg from "utils/showSuccessMsg";
import HandleErrors from "utils/handleErrors";
import TextInput from "components/inputs/TextInput";
import usePermissions from "hooks/usePermission";
import useGetUserInfo from "hooks/useGetUserInfo";
import useLoadMoreOnScroll from "hooks/useLoadMoreOnScroll";

interface EditUsersProps {
    containerStyle?: string;
    isVisible: boolean;
    setIsVisible: (value: boolean) => void;
    roleName?: string;
    selectedStaffIds: number[];
    roleId: number;
    isDefault?: boolean;
    isSuperUserRole?: boolean;
}

export default function EditUsers({
    containerStyle,
    isVisible,
    setIsVisible,
    roleName = "",
    selectedStaffIds = [],
    roleId,
    isDefault,
    isSuperUserRole,
}: EditUsersProps) {
    const userInfo = useGetUserInfo();
    const [updateRole, { isLoading: isUpdatingRole }] =
        useUpdateRoleUsersMutation();

    const [updateSuperUsers, { isLoading: isUpdatingSuperUsers }] =
        useUpdateSuperUsersMutation();

    const [updateRoleName, { isLoading: isUpdatingRoleName }] =
        useUpdateRolePermissionsMutation();

    const [searchValue, setsearchValue] = useState("");
    const [page, setPage] = useState(1);
    const { hasPermissionTo } = usePermissions();
    const assignRolesPermission = hasPermissionTo("assign_roles_permission");

    const [selectedUsers, setSelectedUsers] =
        useState<number[]>(selectedStaffIds);
    const selectedCount = selectedUsers.length;
    const [changedRoleName, setChangedRoleName] = useState(roleName);
    const {
        data: usersData,
        isFetching: isFetchingUsers,
        isError: isUsersError,
        refetch: refetchUsers,
    } = useGetPreviewUsersInfiniteQuery(
        {
            page,
            search: searchValue,
            ...(!isSuperUserRole ? { is_superuser: false } : {}),
        },
        {
            skip: !isVisible,
        },
    );
    const users = usersData?.results ?? [];
    const hasNextPage = Boolean(usersData?.next);
    const staffIdsKey = selectedStaffIds.join(",");
    
    useEffect(() => {
        // Reset on close as well, so reopening never requests a stale page
        setsearchValue("");
        setPage(1);
        if (isVisible) {
            setChangedRoleName(roleName);
            setSelectedUsers(selectedStaffIds);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible, roleId, staffIdsKey]);

    const onSearchChange = (value: string) => {
        setsearchValue(value);
        setPage(1);
    };

    // Request the page after the last loaded one; repeated calls resolve to the
    // same page number, so the observer firing twice can't send duplicate requests
    const loadNextPage = useCallback(() => {
        if (usersData) setPage(usersData.page + 1);
    }, [usersData]);

    const sentinelRef = useLoadMoreOnScroll({
        hasNextPage,
        isFetching: isFetchingUsers,
        isError: isUsersError,
        onLoadMore: loadNextPage,
    });

    const handleUpdateRole = () => {
        console.log(isSuperUserRole, "isSuperUserRole");

        if (isSuperUserRole) {
            console.log(selectedUsers, "selectedUsers");
            updateSuperUsers({ staff_ids: selectedUsers })
                .unwrap()
                .then(() => {
                    showSuccessMsg({ msg: "Role updated successfully" });
                    setIsVisible(false);
                })
                .catch(HandleErrors);
        } else {
            updateRole({ id: roleId, staff_ids: selectedUsers })
                .unwrap()
                .then(() => {
                    showSuccessMsg({ msg: "Role updated successfully" });
                    setIsVisible(false);
                })
                .catch(HandleErrors);
        }
        if (roleName !== changedRoleName) {
            handleUpdateRoleName();
        }
    };

    const handleUpdateRoleName = () => {
        updateRoleName({ id: roleId, name: changedRoleName })
            .unwrap()
            .then(() => {
                showSuccessMsg({ msg: "Role name updated successfully" });
                setIsVisible(false);
            })
            .catch(HandleErrors);
    };

    const isUserLocked = (id: number) =>
        (isDefault && selectedStaffIds.includes(id)) || userInfo?.id === id;

    // Only touch the loaded users so selections on pages not loaded yet are kept
    const handleSelectAll = () => {
        const loadedIds = users.map((u: any) => u.id);
        const isAllInListSelected =
            loadedIds.length > 0 &&
            loadedIds.every((id: number) => selectedUsers.includes(id));
        if (isAllInListSelected) {
            setSelectedUsers((prev) =>
                prev.filter((id) => !loadedIds.includes(id) || isUserLocked(id)),
            );
        } else {
            setSelectedUsers((prev) => Array.from(new Set([...prev, ...loadedIds])));
        }
    };

    const toggleUser = (id: number) => {
        setSelectedUsers((prev) =>
            prev.includes(id) ? prev.filter((uid) => uid !== id) : [...prev, id],
        );
    };

    const handleClose = () => {
        setsearchValue(" ")
        setIsVisible(false)
    }


    return (
        <Drawer
            title={
                <div className={styles.drawerHeader}>
                    <Text className={styles.title}>Edit users Role</Text>
                    <Text className={styles.subtitle}>
                        Manage which users are assigned to this role.
                    </Text>
                </div>
            }
            headerStyle={{ borderBottom: "1px #D9D9D9 solid", paddingBlock: "24px" }}
            placement="right"
            destroyOnClose
            onClose={handleClose}
            open={isVisible}
            closeIcon={false}
            extra={
                <div onClick={() => setIsVisible(false)} className={styles.closeIcon}>
                    <IoMdClose size={28} color="dark" />
                </div>
            }
            width={"40%"}
            className={`${styles.drawer} ${containerStyle}`}
        >
            <div className={styles.drawerContent}>
                <div className={styles.roleHeader}>
                    {/* <Text className={styles.roleName}>{roleName}</Text> */}
                    <TextInput
                        label="Role Name"
                        value={changedRoleName}
                        containerStyle="flex-grow-1"
                        disabled={isSuperUserRole}
                        onChange={(e) => {
                            setChangedRoleName(e.target.value);
                        }}
                    />
                </div>

                <DebouncedSearch
                    placeholder="search users"
                    time={500}
                    onSearchChange={onSearchChange}
                />

                <div className={styles.usersListSection}>
                    <div className={styles.usersListHeader}>
                        <div className="d-flex flex-column gap-1">
                            <Text className={styles.usersListTitle}>Users in this role</Text>
                            <Text className={styles.usersListSubtitle}>
                                {selectedCount} selected / {usersData?.count ?? 0} user
                            </Text>
                        </div>
                        <span className={styles.selectAll} onClick={handleSelectAll}>
                            Select All
                        </span>
                    </div>

                    <div className={styles.usersList}>
                        {/* Initial load, or a new search replacing the list */}
                        {isFetchingUsers && page === 1 && <Spin className="m-auto" />}
                        {!isFetchingUsers && !isUsersError && users.length === 0 && (
                            <Empty description="No users found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )}
                        {users.map((item: any) => (
                            <div key={item.id} className={styles.userRow}>
                                <UserInfo data={{ user: item, ...item }} isTableCol />
                                <Checkbox
                                    className={styles.checkbox}
                                    checked={selectedUsers.includes(item.id)}
                                    disabled={isUserLocked(item.id)}
                                    onChange={() => toggleUser(item.id)}
                                />
                            </div>
                        ))}
                        {isFetchingUsers && page > 1 && <Spin className="m-auto" />}
                        {isUsersError && !isFetchingUsers && (
                            <div className={styles.errorState}>
                                <Text>Couldn&apos;t load users.</Text>
                                <Button isOutline onClick={() => refetchUsers()}>
                                    Retry
                                </Button>
                            </div>
                        )}
                        <div ref={sentinelRef} aria-hidden />
                    </div>
                </div>
            </div>

            <div className={styles.footer}>
                <Button
                    btnClassName={styles.cancelBtn}
                    isOutline
                    onClick={() => setIsVisible(false)}
                >
                    Cancel
                </Button>
                {assignRolesPermission && (
                    <Button
                        btnClassName={styles.saveBtn}
                        onClick={handleUpdateRole}
                        isLoading={
                            isUpdatingRole || isUpdatingSuperUsers || isUpdatingRoleName
                        }
                    >
                        Update Role
                    </Button>
                )}
            </div>
        </Drawer>
    );
}
